import { create } from 'zustand';
import { createRoom } from '../lib/api';
import { DEFAULT_CHUNK_SIZE } from '../lib/config';
import { FileSender } from '../lib/transfer/sender';
import type { FileMetadata } from '../lib/transfer/protocol';
import type { TransferPhase, TransferStats } from '../lib/transfer/types';
import { SignalingClient } from '../lib/webrtc/signaling';

interface SenderState {
  phase: TransferPhase;
  file: File | null;
  meta: FileMetadata | null;
  roomId: string | null;
  expiresAt: string | null;
  shareUrl: string | null;
  stats: TransferStats | null;
  sha256: string | null;
  error: string | null;
  creating: boolean;

  createTransfer: (file: File) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

// Engine instances live outside React state (they are not serializable).
let sender: FileSender | null = null;
let signaling: SignalingClient | null = null;

function teardown(): void {
  sender?.destroy();
  signaling?.close();
  sender = null;
  signaling = null;
}

export const useSenderStore = create<SenderState>((set) => ({
  phase: 'idle',
  file: null,
  meta: null,
  roomId: null,
  expiresAt: null,
  shareUrl: null,
  stats: null,
  sha256: null,
  error: null,
  creating: false,

  createTransfer: async (file: File) => {
    teardown();
    set({
      creating: true,
      error: null,
      file,
      phase: 'idle',
      stats: null,
      sha256: null,
      meta: null,
    });
    try {
      const { roomId, expiresAt } = await createRoom();
      const shareUrl = `${window.location.origin}/share/${roomId}`;

      signaling = new SignalingClient(roomId, 'sender');
      sender = new FileSender(signaling, file, { chunkSize: DEFAULT_CHUNK_SIZE });

      sender.on('phase', (phase) => set({ phase }));
      sender.on('meta', (meta) => set({ meta }));
      sender.on('progress', (stats) => set({ stats }));
      sender.on('done', ({ sha256 }) => set({ sha256, phase: 'complete' }));
      sender.on('error', ({ message }) => set({ error: message, phase: 'error' }));

      signaling.connect();
      sender.start();

      set({ roomId, expiresAt, shareUrl, creating: false, phase: 'waiting-for-peer' });
    } catch (err) {
      set({ error: (err as Error).message, creating: false, phase: 'error' });
    }
  },

  cancel: () => {
    sender?.cancel();
    set({ phase: 'cancelled' });
  },

  reset: () => {
    teardown();
    set({
      phase: 'idle',
      file: null,
      meta: null,
      roomId: null,
      expiresAt: null,
      shareUrl: null,
      stats: null,
      sha256: null,
      error: null,
      creating: false,
    });
  },
}));
